import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useBeforeUnload, useNavigate, useParams } from "react-router-dom";
import {
  useCreateMoveMutation,
  useUpdateMoveMutation,
  useGetMoveQuery,
} from "@/store/moves/apis";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CaretDownIcon } from "@radix-ui/react-icons";
import { Addresses } from "../EditJob/Addresses";
import CustomerInfo from "../EditMove/CustomerInfo";
import Service from "../EditJob/Service";
import Billing from "../EditJob/Billing";
import Notes from "../EditMove/Notes";
import type { MoveInputs } from "@/types";
import * as z from "zod";
import { setEditingMove } from "@/store/session/slice";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import Title from "../EditMove/title";
import { Loading } from "@/components/Loading";
import Schedule from "../EditJob/Schedule";
import {
  AlertDialog,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const estimateRequiredFields = {
  customer_name: "Customer Name",
  customer_mobile_phone: "Customer Mobile Phone",
  customer_email: "Customer Email",
  service: "Service",
  service_date: "Service Date",
  origin: "Origin City and State",
};

const bookMoveRequiredFields = {
  customer_name: "Customer Name",
  customer_mobile_phone: "Customer Mobile Phone",
  customer_email: "Customer Email",
  service: "Service",
  service_date: "Service Date",
  origin: "Origin Address",
};

const estimateMoveSchema = z.object({
  customer_name: z.string().min(2, "(required)"),
  customer_mobile_phone: z.string().min(10, "(required)"),
  customer_email: z.string().email("(valid email required)"),
  service: z
    .string()
    .nullable()
    .refine(
      (val) => val !== null && val !== undefined && val !== "",
      "(required)"
    ),
  service_date: z.coerce
    .date()
    .transform((date) => date.toISOString().split("T")[0])
    .nullable()
    .refine(
      (val) => val !== null && val !== undefined && val !== "",
      "(required)"
    ),
  origin: z.object({
    city: z
      .string()
      .nullable()
      .refine(
        (val) => val !== null && val !== undefined && val !== "",
        "(required)"
      ),
    state: z
      .string()
      .nullable()
      .refine(
        (val) => val !== null && val !== undefined && val !== "",
        "(required)"
      ),
  }),
});

const bookMoveSchema = z
  .object({
    customer_name: z.string().min(2, "(required)"),
    customer_mobile_phone: z.string().min(10, "(required)"),
    customer_email: z.string().email("(required)"),
    service: z
      .string()
      .nullable()
      .refine(
        (val) => val !== null && val !== undefined && val !== "",
        "(required)"
      ),
    service_date: z.coerce
      .date()
      .transform((date) => date.toISOString().split("T")[0])
      .nullable()
      .refine(
        (val) => val !== null && val !== undefined && val !== "",
        "(required)"
      ),
    origin: z.object({
      street: z
        .string()
        .nullable()
        .refine(
          (val) => val !== null && val !== undefined && val !== "",
          "(required)"
        ),
      city: z
        .string()
        .nullable()
        .refine(
          (val) => val !== null && val !== undefined && val !== "",
          "(required)"
        ),
      state: z
        .string()
        .nullable()
        .refine(
          (val) => val !== null && val !== undefined && val !== "",
          "(required)"
        ),
      zipcode: z
        .string()
        .nullable()
        .refine(
          (val) => val !== null && val !== undefined && val !== "",
          "(required)"
        ),
    }),
    start_window_begin_hour: z.number().nullable(),
    start_window_begin_minute: z.number().nullable(),
    start_window_begin_meridiem: z.string().nullable(),
    start_window_end_hour: z.number().nullable(),
    start_window_end_minute: z.number().nullable(),
    start_window_end_meridiem: z.string().nullable(),
  })
  .refine(
    (data) => {
      const isValid = [
        data.start_window_begin_hour,
        data.start_window_begin_minute,
        data.start_window_begin_meridiem,
        data.start_window_end_hour,
        data.start_window_end_minute,
        data.start_window_end_meridiem,
      ].every((value) => value !== undefined && value !== null && value !== "");

      return isValid;
    },
    {
      message: "(required)",
      path: ["start_window_begin", "start_window_end"],
    }
  );

export default function EditJob({ ts }: { ts: number }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();
  const mover = useSelector(getSelectedMover);
  const [createMove] = useCreateMoveMutation();
  const [updateMove] = useUpdateMoveMutation();
  const { data: existingMove, isLoading } = useGetMoveQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });
  const { toast } = useToast();
  const [showError, setShowError] = useState(false);

  const form = useForm<MoveInputs>({
    defaultValues: {
      customer_id: null,
      customer_name: "",
      customer_mobile_phone: "",
      customer_alternate_phone: "",
      customer_email: "",
      customer_source: "",
      customer_source_other: "",
      can_text_primary: false,
      can_text_secondary: false,
      service: null,
      notes_service: null,
      status: "initial",
      service_date: null,
      start_window_begin_hour:
        mover?.mover_configuration?.default_start_window?.begin_hour || null,
      start_window_begin_minute:
        mover?.mover_configuration?.default_start_window?.begin_minute || null,
      start_window_begin_meridiem:
        mover?.mover_configuration?.default_start_window?.begin_meridiem ||
        null,
      start_window_end_hour:
        mover?.mover_configuration?.default_start_window?.end_hour || null,
      start_window_end_minute:
        mover?.mover_configuration?.default_start_window?.end_minute || null,
      start_window_end_meridiem:
        mover?.mover_configuration?.default_start_window?.end_meridiem || null,
      kind: null,
      invoice: {
        total: null,
      },
      line_items: [],
      origin: {
        street: "",
        city: "",
        state: "",
        zipcode: "",
        number: "",
        name: "",
        floor: null,
        elevator: null,
      },
    },
  });

  const {
    formState: { errors },
  } = form;

  useEffect(() => {
    dispatch(setEditingMove(true));

    // Cleanup function to set editingMove to false when component unmounts
    return () => {
      dispatch(setEditingMove(false));
    };
  }, [dispatch]);

  // Prompt the user before they leave the page
  useBeforeUnload(
    React.useCallback((event) => {
      event.preventDefault();
      return "Are you sure you want to leave?";
    }, [])
  );

  // unstable_usePrompt({
  //   message:
  //     "You are working on a move and may lose unsaved changes. Are you sure you want to leave?",
  //   when: ({ currentLocation, nextLocation }) =>
  //     currentLocation.pathname !== nextLocation.pathname,
  // });

  useEffect(() => {
    if (id) {
      form.reset(existingMove);
    }
  }, [ts, id, form, existingMove]);

  const massagePayloadForSubmit = (data: MoveInputs) => {
    const { origin, invoice, line_items, ...rest } = data;
    return {
      ...rest,
      invoice_attributes: {
        ...invoice,
      },
      line_items_attributes: line_items.map((item) => ({
        ...item,
      })),
      origin_attributes: {
        ...origin,
      },
      book_move: data.book_move,
      estimate_move: data.estimate_move,
      send_estimate: data.send_estimate,
      send_booking_confirmation: data.send_booking_confirmation,
    };
  };

  const validateAndSubmit = async (data: MoveInputs) => {
    // Clear all previous errors first
    form.clearErrors();
    setShowError(false);

    if (
      data.status === "booked" ||
      data.status === "quoted" ||
      data.book_move ||
      data.estimate_move ||
      data.send_estimate ||
      data.send_booking_confirmation
    ) {
      let validationResult;
      if (
        data.book_move ||
        data.status === "booked" ||
        data.send_booking_confirmation
      ) {
        validationResult = bookMoveSchema.safeParse(data);
      } else {
        validationResult = estimateMoveSchema.safeParse(data);
      }

      if (!validationResult.success) {
        const errors = validationResult.error.formErrors.fieldErrors;

        // Set all errors at once using setError
        Object.entries(errors).forEach(([key, value]) => {
          type ErrorKey = keyof typeof errors;
          if (value && value.length > 0) {
            form.setError(key as ErrorKey, {
              type: "manual",
              message: value[0],
            });
          }
        });

        setShowError(true);

        return false;
      }
    }

    const payload = massagePayloadForSubmit(data);
    try {
      let res;
      if (id) {
        res = await updateMove({ id, move: payload });
      } else {
        res = await createMove(payload);
      }

      if (res?.data?.id) {
        navigate(`/job/${res.data.id}`, {
          state: {
            toast: `Your job has been ${
              id ? "updated" : "created"
            } successfully`,
          },
        });
        return true;
      } else {
        // TODO: log error with a library
        toast({
          title:
            "This move could not be saved. We are looking into it. Please don't exit the page.",
          variant: "destructive",
        });
        return false; // Submission failed
      }
    } catch (error) {
      // TODO: log error with a library
      console.log("--- error ---", error);
      toast({
        title:
          "This move could not be saved. We are looking into it. Please don't exit the page.",
        variant: "destructive",
      });
      return false; // Submission failed
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="border-b border-black mb-24">
      <Form {...form}>
        {/* <form onSubmit={form.handleSubmit(onSubmit)}> */}
        <form>
          <div className="hidden md:flex w-full px-4 py-2 sticky top-24 left-0 justify-between items-center bg-white h-18 z-[1] border-b border-black">
            <div className="w-full md:w-auto">
              <div className="flex flex-row gap-2 items-end">
                <div className="ml-4 flex flex-row gap-3">
                  {id && (
                    <Button
                      type="button"
                      variant="secondary"
                      className="text-black"
                      onClick={() =>
                        window.confirm(
                          "Are you sure you want to cancel editing this move?"
                        ) && navigate(`/move/${id}`)
                      }
                    >
                      Cancel
                    </Button>
                  )}
                  <div className="flex items-center">
                    <Button
                      type="button"
                      variant="default"
                      className={"rounded-r-none shadow-none"}
                      onClick={async (e) => {
                        e.preventDefault();
                        form.setValue("send_estimate", false);
                        form.setValue("book_move", false);
                        form.setValue("send_booking_confirmation", false);
                        form.setValue("estimate_move", false);
                        await validateAndSubmit(form.getValues());
                      }}
                    >
                      Save Move
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          className={
                            "rounded-l-none bg-yellow-500 hover:bg-yellow-500/90 px-2 py-2 h-9"
                          }
                        >
                          <CaretDownIcon />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="bottom" align="end">
                        {id ? (
                          <>
                            {(form.watch("status") === "quoted" ||
                              form.watch("status") === "initial") && (
                              <>
                                <DropdownMenuItem
                                  onSelect={async () => {
                                    form.setValue("send_estimate", true);
                                    form.setValue("book_move", false);
                                    form.setValue(
                                      "send_booking_confirmation",
                                      false
                                    );
                                    form.setValue("estimate_move", false);
                                    await validateAndSubmit(form.getValues());
                                  }}
                                >
                                  Save and Send Estimate
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onSelect={async () => {
                                    form.setValue("book_move", true);
                                    form.setValue("send_estimate", false);
                                    form.setValue(
                                      "send_booking_confirmation",
                                      false
                                    );
                                    form.setValue("estimate_move", false);
                                    await validateAndSubmit(form.getValues());
                                  }}
                                >
                                  Save and Book
                                </DropdownMenuItem>
                              </>
                            )}
                            {form.watch("status") === "booked" && (
                              <>
                                <DropdownMenuItem
                                  onSelect={async () => {
                                    form.setValue(
                                      "send_booking_confirmation",
                                      true
                                    );
                                    form.setValue("send_estimate", false);
                                    form.setValue("book_move", false);
                                    form.setValue("estimate_move", false);
                                    await validateAndSubmit(form.getValues());
                                  }}
                                >
                                  Save and Send Confirmation
                                </DropdownMenuItem>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <DropdownMenuItem
                              onSelect={async () => {
                                form.setValue("estimate_move", true);
                                form.setValue("send_estimate", false);
                                form.setValue("book_move", false);
                                form.setValue(
                                  "send_booking_confirmation",
                                  false
                                );
                                await validateAndSubmit(form.getValues());
                              }}
                            >
                              Save and Send Estimate
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={async () => {
                                form.setValue("book_move", true);
                                form.setValue("send_estimate", false);
                                form.setValue(
                                  "send_booking_confirmation",
                                  false
                                );
                                form.setValue("estimate_move", false);
                                await validateAndSubmit(form.getValues());
                              }}
                            >
                              Save and Book
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="md:grid md:grid-cols-12">
            <div className="md:col-span-10">
              <div className="grid grid-cols-10">
                <div
                  className={cn(
                    "col-span-10 md:col-span-4 pb-8 md:border-r md:border-black"
                  )}
                >
                  <EditSection title="Customer">
                    <CustomerInfo />
                  </EditSection>
                </div>

                <div className={cn("col-span-10 md:col-span-6 pb-8")}>
                  <EditSection title="Notes">
                    <Notes />
                  </EditSection>
                </div>
              </div>

              <div
                className={cn(
                  "col-span-10 md:col-span-6 border-t border-black pb-8"
                )}
              >
                <EditSection title="Service Details">
                  <Service />
                </EditSection>
              </div>

              <div
                className={cn(
                  "col-span-10 md:col-span-6 border-t border-black pb-8"
                )}
              >
                <EditSection title="Schedule">
                  <Schedule />
                </EditSection>
              </div>

              <div
                className={cn(
                  "col-span-10 md:col-span-6 border-t border-black pb-8"
                )}
              >
                <EditSection title="Locations">
                  <Addresses />
                </EditSection>
              </div>

              <div
                className={cn(
                  "col-span-10 md:col-span-6 border-t border-black pb-8"
                )}
              >
                <EditSection title="Estimate of Charges">
                  <Billing />
                </EditSection>
              </div>
            </div>
          </div>
        </form>
      </Form>

      <AlertDialog open={showError} onOpenChange={setShowError}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base">
              Please fix the fields marked{" "}
              <span className="text-red-500">
                <FontAwesomeIcon icon={faXmark} />
              </span>
              {(form.getValues("book_move") ||
                form.getValues("send_booking_confirmation")) &&
                " to book the move"}
              {(form.getValues("estimate_move") ||
                form.getValues("send_estimate")) &&
                " to estimate the move"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-black">
              <div
                className={cn(
                  "hidden",
                  (form.getValues("estimate_move") ||
                    form.getValues("send_estimate")) &&
                    "block space-y-0.5"
                )}
              >
                {Object.entries(estimateRequiredFields).map(
                  ([field, label]) => (
                    <div className="flex flex-row gap-2" key={field}>
                      {errors[field as keyof MoveInputs]?.message ? (
                        <span className="text-red-500">
                          <FontAwesomeIcon icon={faXmark} />
                        </span>
                      ) : (
                        <span className="text-green-500">
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                      {label}
                    </div>
                  )
                )}
              </div>
              <div
                className={cn(
                  "hidden",
                  (form.getValues("book_move") ||
                    form.getValues("send_booking_confirmation")) &&
                    "block space-y-0.5"
                )}
              >
                {Object.entries(bookMoveRequiredFields).map(
                  ([field, label]) => (
                    <div className="flex flex-row gap-2" key={field}>
                      {errors[field as keyof MoveInputs]?.message ? (
                        <span className="text-red-500">
                          <FontAwesomeIcon icon={faXmark} />
                        </span>
                      ) : (
                        <span className="text-green-500">
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                      {label}
                    </div>
                  )
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>OK</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const EditSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <>
      <div className="bg-stone-50 px-8 py-1">
        <div className="hidden md:block">
          <Title title={title} />
        </div>
      </div>
      <div className="mt-6 container mx-auto px-8">{children}</div>
    </>
  );
};
